import { currency } from '../../utils/currency';
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, message, Spin, Alert, Typography, Space } from 'antd';
import { PlusOutlined, FileExcelOutlined } from '@ant-design/icons';
import { CashFlow, CashFlowsResponse } from '../../types';
import { useGlobalContext } from '../../store/GlobalContext';
import axios, { AxiosError } from 'axios';
import { CashFlowService } from './cashflow.service';
import CashFlowDetails from './cashflowdetail';
import AddEditCashFlowForm from './cashflowform';
import ImportExportModal from '../../components/ImportExportModal';

const CashFlowListPage: React.FC = () => {
  const [cashFlows, setCashFlows] = useState<CashFlow[]>([]);
  const [selectedCashFlow, setSelectedCashFlow] = useState<CashFlow | null>(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState<boolean>(false);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  // Fetch cash flows when component mounts or estateId changes
  useEffect(() => {
    if (estateId) {
      fetchCashFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estateId]);

  const fetchCashFlows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await CashFlowService.fetchCashFlows({ estateId, page: 1, limit: 100 }); // Adjust pagination as needed
      setCashFlows(response.data);
    } catch (err: any) {
      console.error(err);
      // Handle structured 400 errors differently if needed
      if (axios.isAxiosError(err) && err.response?.status === 400 && Array.isArray(err.response.data.message)) {
        setError('Validation errors occurred. Please check your input.');
      } else {
        setError(err.message || 'Failed to fetch cash flows.');
      }
    } finally {
      setLoading(false);
    }
  }, [estateId]);

  const showCashFlowDetails = (cashFlow: CashFlow) => {
    setSelectedCashFlow(cashFlow);
    setIsDetailsModalVisible(true);
  };

  const closeCashFlowDetails = () => {
    setSelectedCashFlow(null);
    setIsDetailsModalVisible(false);
  };

  const showAddForm = () => {
    setSelectedCashFlow(null);
    setIsFormVisible(true);
  };

  const closeAddEditForm = () => {
    setSelectedCashFlow(null);
    setIsFormVisible(false);
  };

  /**
   * Handle Add or Edit CashFlow
   * @param cashFlow - CashFlow data from the form
   */
  const handleAddEditCashFlow = async (cashFlow: CashFlow) => {
    setLoading(true);
    try {
      if (cashFlow.id) {
        // Update existing cash flow
        await CashFlowService.updateCashFlow(estateId, cashFlow.id, cashFlow);
        message.success('Cash flow updated successfully.');
      } else {
        // Create new cash flow
        await CashFlowService.createCashFlow(estateId, cashFlow);
        message.success('Cash flow added successfully.');
      }
      await fetchCashFlows(); // Refresh the list
      closeAddEditForm();
    } catch (err: any) {
      console.error(err);
      // Re-throw the error to let the form handle it
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle Delete CashFlow
   * @param cashFlowId - ID of the CashFlow to delete
   */
  const handleDeleteCashFlow = async (cashFlowId: string) => {
    setLoading(true);
    try {
      await CashFlowService.deleteCashFlow(estateId, cashFlowId);
      message.success('Cash flow deleted successfully.');
      await fetchCashFlows(); // Refresh the list
      closeCashFlowDetails();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to delete cash flow.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', width: 160, fixed: 'left' as const, render: (name: string, record: CashFlow) => <Button type="link" style={{ whiteSpace: 'normal', height: 'auto', padding: 0, textAlign: 'left' }} onClick={() => showCashFlowDetails(record)}>{name || 'Unnamed cashflow'}</Button> },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    {
      title: 'Payment Amount',
      dataIndex: 'amount',
      key: 'amount',
      className: 'financial-value', render: (value: number) => currency(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CashFlow) => (
        <Button type="link" onClick={() => showCashFlowDetails(record)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="record-page" style={{ padding: '24px' }}>
      <Typography.Title level={1} style={{ fontSize: 28 }}>Cashflows</Typography.Title>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Cash Flow List</span>
            <Space>
              <Button
                icon={<FileExcelOutlined />}
                onClick={() => setIsImportModalVisible(true)}
              >
                Import Cashflows
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddForm}>
                Add Cash Flow
              </Button>
            </Space>
          </div>
        }
        style={{ marginTop: '24px' }}
        extra={null}
      >
        <Typography.Paragraph>
          View and manage all cash flows associated with your estate.
        </Typography.Paragraph>
        {error && (
          <Alert
            message={error} action={<Button onClick={() => fetchCashFlows()}>Retry</Button>}
            type="error"
            showIcon
            style={{ margin: '16px 0' }}
          />
        )}
        <p className="table-scroll-hint">Scroll within the table for more columns.</p>
        {!error && <Spin spinning={loading}>
          <Table
            locale={{ emptyText: loading ? 'Loading records...' : 'No records found for this view.' }}
            columns={columns}
            dataSource={loading ? [] : cashFlows}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            scroll={{ x: 'max-content' }}
          />
        </Spin>}
      </Card>

      <AddEditCashFlowForm
        open={isFormVisible}
        onClose={closeAddEditForm}
        initialData={selectedCashFlow || undefined}
        onSubmit={handleAddEditCashFlow} 
        estateId={estateId}
      />

      <CashFlowDetails
        open={isDetailsModalVisible}
        onClose={closeCashFlowDetails}
        cashFlow={selectedCashFlow}
        onEdit={(cashFlow: CashFlow) => {
          setSelectedCashFlow(cashFlow);
          setIsFormVisible(true);
        }}
        onDelete={handleDeleteCashFlow}
      />

      {estateId && (
        <ImportExportModal
          open={isImportModalVisible}
          onClose={() => setIsImportModalVisible(false)}
          estateId={estateId}
        />
      )}
    </div>
  );
};

export default CashFlowListPage;
